import { useEffect, useState } from "react";
import { useAuth, sendVerificationEmail, signOut } from "../auth";
import { firebaseAuth } from "../lib/firebase-app";

/**
 * Blocks the app until the signed-in user's email is verified. Rendered from
 * App for EVERY authenticated session (signup or login), so there is no way to
 * reach the app with an unverified email — including via the login path.
 */
export function VerifyEmailScreen({ email }: { email: string }) {
  const { refresh } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [checking, setChecking] = useState(false);
  const [notYet, setNotYet] = useState(false);

  // Auto-poll: reload the Firebase user so clicking the emailed link is picked
  // up without the user pressing anything. Parent swaps this screen out the
  // moment emailVerified flips true.
  useEffect(() => {
    const t = setInterval(() => {
      void refresh();
    }, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const resend = async () => {
    setSending(true);
    setNotYet(false);
    try {
      await sendVerificationEmail();
      setSent(true);
    } catch {
      // Firebase rate-limits resends; the link they already have still works.
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  const check = async () => {
    setChecking(true);
    setNotYet(false);
    await refresh();
    // Read straight off currentUser to avoid a stale closure; if still false
    // the parent keeps us mounted and we show the hint.
    if (firebaseAuth().currentUser?.emailVerified === false) setNotYet(true);
    setChecking(false);
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1>Verify your email</h1>
        <p className="subtitle">
          We sent a verification link to <strong>{email}</strong>. Click it, then continue.
        </p>
        <button onClick={() => void check()} disabled={checking}>
          {checking ? "Checking…" : "I've verified — continue"}
        </button>
        {notYet && (
          <div className="banner error">
            Not verified yet. Open the link in your inbox (check spam), then try again.
          </div>
        )}
        <p className="footnote">
          Didn't get it?{" "}
          <button className="btn btn--text" onClick={() => void resend()} disabled={sending}>
            {sending ? "Sending…" : "Resend link"}
          </button>
          {sent && <span> Sent — check your inbox.</span>}
        </p>
        <p className="footnote">
          Wrong account?{" "}
          <button className="btn btn--text" onClick={() => void signOut()}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
